// TopAppBar — MD3 TopAppBar: слева название текущего раздела, справа
// bell-заглушка уведомлений (R42), ThemeToggle + UserMenu (R42: имя+email+аватар).
// D19: лого и «Упряжка» здесь НЕ дублируются — брендинг живёт только в NavRail/SideDrawer.
// R42 / U-13 (research §5.1): gap 8px между actions, dense Toolbar, elevation on scroll
// (useScrollTrigger — бар «поднимается» при прокрутке, MD3 small top app bar).
import { useState } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Box, Tooltip, useScrollTrigger } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useLocation } from 'react-router-dom';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { SideDrawer } from './SideDrawer';
import { t } from '@/i18n';

interface TopAppBarProps {
  /** Callback для открытия SideDrawer (передаётся из AppShell). */
  onMenuClick?: () => void;
}

/** Сопоставление префикса пути → название раздела (порядок важен: от частного к общему). */
const SECTION_TITLES: ReadonlyArray<readonly [string, string]> = [
  ['/crm/deals', 'Сделки'],
  ['/crm/contacts', 'Контакты'],
  ['/crm/companies', 'Компании'],
  // V12 U-34-E: раздел «Лиды» (зеркально NavRail).
  ['/crm/leads', 'Лиды'],
  ['/dashboard', 'Дашборд'],
  ['/tasks', 'Задачи'],
  ['/calendar', 'Календарь'],
  // R39b/R40: новые разделы профиля и админ-раздела.
  ['/profile', 'Мой профиль'],
  ['/admin/team', 'Команда'],
  ['/admin', 'Администрирование'],
];

/** Резолвит название текущего раздела по pathname; неизвестный раздел → пустая строка. */
function resolveSectionTitle(pathname: string): string {
  const match = SECTION_TITLES.find(([prefix]) => pathname.startsWith(prefix));
  return match ? match[1] : '';
}

/**
 * MD3 TopAppBar — общая шапка для desktop и mobile.
 * R42: bell-заглушка (Tooltip «скоро»), gap 8px между actions, elevation on scroll.
 */
export function TopAppBar({ onMenuClick }: TopAppBarProps): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  // R42: при прокрутке контента бар получает elevation (отделение от контента, MD3).
  const scrollTrigger = useScrollTrigger({ threshold: 100 });

  return (
    <>
      <AppBar
        position="sticky"
        color="default"
        elevation={scrollTrigger ? 2 : 0}
        sx={{ transition: 'box-shadow 150ms cubic-bezier(0.2, 0, 0, 1)' }}
      >
        <Toolbar sx={{ gap: 1, pr: { xs: 1.5, md: 2 } }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open navigation"
            onClick={() => (onMenuClick ? onMenuClick() : setDrawerOpen(true))}
            sx={{ display: { sm: 'none' }, mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, flexGrow: 1 }}>
            {resolveSectionTitle(location.pathname)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* R42: уведомления — заглушка (центр уведомлений вне scope, research §6 Could). */}
            <Tooltip title={t('topbar.notifications.tooltip')}>
              <IconButton
                color="inherit"
                aria-label={t('topbar.notifications.label')}
                data-testid="notifications-bell"
              >
                <NotificationsIcon />
              </IconButton>
            </Tooltip>
            <ThemeToggle />
            <UserMenu />
          </Box>
        </Toolbar>
      </AppBar>
      <SideDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}
